import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { X, Download, UploadCloud, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2, ArrowLeft } from 'lucide-react';
import { validatePropertyImport, commitPropertyImport } from '../lib/api';

const TEMPLATE_HEADER = 'Name,Address,AverageCleaningTime,Bedroom1,Bedroom2,Bedroom3,Bedroom4,Bedroom5,Bedroom6,Bedroom7,Bedroom8,Bedroom9,Bedroom10';
const TEMPLATE_ROWS = [
  'EXAMPLE - Ocean View Suite (replace me),"123 Beach Road, Miami",60,"1 King, 1 Double","2 Single",,,,,,,,',
  'EXAMPLE - City Loft (replace me),"45 High Street, London",45,"1 Queen",,,,,,,,,',
];

function downloadTemplate() {
  const csv = [TEMPLATE_HEADER, ...TEMPLATE_ROWS].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'properties_template.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function BulkImportModal({ open, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [validating, setValidating] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setValidating(false);
    setImporting(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = async (selected) => {
    if (!selected) return;
    if (!selected.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please select a .csv file');
      return;
    }
    setFile(selected);
    setValidating(true);
    setPreview(null);
    try {
      const res = await validatePropertyImport(selected);
      setPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not read the file');
      setFile(null);
    } finally {
      setValidating(false);
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    try {
      const res = await commitPropertyImport(file);
      const { imported, skipped_limit } = res.data;
      if (imported > 0) {
        toast.success(`${imported} propert${imported === 1 ? 'y' : 'ies'} imported`);
      } else {
        toast.error('No properties were imported');
      }
      if (skipped_limit > 0) {
        toast.error(`${skipped_limit} skipped: plan limit reached`);
      }
      onImported?.();
      handleClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const noSlots = preview && preview.remaining_slots === 0;
  const capped = preview && preview.remaining_slots > 0 && preview.valid_count > preview.remaining_slots;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={handleClose}
          data-testid="bulk-import-modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-500 to-emerald-500 p-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center">
                  <FileSpreadsheet size={24} className="text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Bulk Import Properties</h2>
                  <p className="text-white/80 text-xs">Upload a CSV to add many at once</p>
                </div>
              </div>
              <button onClick={handleClose} className="text-white/80 hover:text-white p-1" data-testid="close-import-modal">
                <X size={22} />
              </button>
            </div>

            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {/* Step 1: upload */}
              {!preview && (
                <div className="space-y-5">
                  <button
                    onClick={downloadTemplate}
                    className="w-full flex items-center justify-center gap-2 h-11 rounded-xl border-2 border-slate-200 text-slate-700 font-semibold text-sm hover:bg-slate-50 transition-colors"
                    data-testid="download-template-button"
                  >
                    <Download size={18} /> Download CSV template
                  </button>

                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-slate-300 rounded-2xl p-8 text-center cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/40 transition-colors"
                    data-testid="upload-dropzone"
                  >
                    {validating ? (
                      <div className="flex flex-col items-center gap-3 text-slate-500">
                        <Loader2 size={28} className="animate-spin text-emerald-500" />
                        <span className="text-sm font-medium">Reading {file?.name}...</span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center">
                          <UploadCloud size={28} className="text-emerald-500" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-800">Click to upload your CSV</p>
                          <p className="text-xs text-slate-400 mt-0.5">Name is required. Bedrooms like "1 King, 1 Double".</p>
                        </div>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      className="hidden"
                      onChange={(e) => handleFile(e.target.files?.[0])}
                      data-testid="csv-file-input"
                    />
                  </div>
                </div>
              )}

              {/* Step 2: preview */}
              {preview && (
                <div className="space-y-4" data-testid="import-preview">
                  <button
                    onClick={reset}
                    className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 font-medium"
                  >
                    <ArrowLeft size={16} /> Choose a different file
                  </button>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100" data-testid="preview-valid-count">
                      <div className="flex items-center gap-2 text-emerald-600">
                        <CheckCircle2 size={18} />
                        <span className="text-2xl font-bold">{preview.valid_count}</span>
                      </div>
                      <p className="text-xs font-medium text-emerald-700 mt-1">Ready to import</p>
                    </div>
                    <div className={`rounded-xl p-4 border ${preview.error_count > 0 ? 'bg-rose-50 border-rose-100' : 'bg-slate-50 border-slate-100'}`} data-testid="preview-error-count">
                      <div className={`flex items-center gap-2 ${preview.error_count > 0 ? 'text-rose-600' : 'text-slate-400'}`}>
                        <AlertTriangle size={18} />
                        <span className="text-2xl font-bold">{preview.error_count}</span>
                      </div>
                      <p className={`text-xs font-medium mt-1 ${preview.error_count > 0 ? 'text-rose-700' : 'text-slate-400'}`}>With errors (skipped)</p>
                    </div>
                  </div>

                  {noSlots && (
                    <div className="flex items-start gap-2 bg-rose-50 border border-rose-100 rounded-xl p-3 text-xs text-rose-700">
                      <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                      <span>Your plan limit is reached. Upgrade to import more properties.</span>
                    </div>
                  )}
                  {capped && (
                    <div className="flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-xl p-3 text-xs text-amber-700">
                      <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                      <span>Only {preview.remaining_slots} slot(s) left on your plan; the rest will be skipped.</span>
                    </div>
                  )}

                  {/* Row list */}
                  <div className="max-h-64 overflow-y-auto space-y-2 pr-1">
                    {preview.rows.map((r) => (
                      <div
                        key={r.row}
                        className={`rounded-xl p-3 border text-sm ${r.valid ? 'bg-white border-slate-100' : 'bg-rose-50/60 border-rose-100'}`}
                        data-testid={`preview-row-${r.row}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold text-slate-800 truncate">
                            {r.name || <span className="italic text-slate-400">No name</span>}
                          </span>
                          {r.valid ? (
                            <CheckCircle2 size={16} className="text-emerald-500 flex-shrink-0" />
                          ) : (
                            <AlertTriangle size={16} className="text-rose-500 flex-shrink-0" />
                          )}
                        </div>
                        {r.valid && (
                          <p className="text-xs text-slate-400 mt-0.5">
                            {r.bedroomsCount} bedroom{r.bedroomsCount === 1 ? '' : 's'} · {r.totalBeds} bed{r.totalBeds === 1 ? '' : 's'}
                            {r.averageCleaningTime ? ` · ${r.averageCleaningTime} min` : ''}
                          </p>
                        )}
                        {!r.valid && (
                          <ul className="text-xs text-rose-600 mt-1 list-disc list-inside">
                            {r.errors.map((e, i) => <li key={i}>{e}</li>)}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            {preview && (
              <div className="p-6 pt-0 flex gap-3">
                <button
                  onClick={handleClose}
                  className="flex-1 h-12 rounded-xl font-semibold border-2 border-slate-200 hover:bg-slate-50 text-slate-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleImport}
                  disabled={importing || preview.valid_count === 0 || noSlots}
                  className="flex-1 h-12 rounded-xl font-semibold bg-gradient-to-r from-blue-500 to-emerald-500 text-white hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="confirm-import-button"
                >
                  {importing ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />}
                  {importing ? 'Importing...' : `Import ${preview.valid_count}`}
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
